import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Button, Grid, Stack, Typography,
} from '@mui/material';
import { saveSiteConfiguration } from '../../../../features/Configuration/configurationAction';
import { InfoChip } from '../../../../components/CustomChip';
import useToggle from '../../../../hooks/useToggle';
import { openSnackbar } from '../../../../features/SnackBar/snackBarSlice';
import { SelectBox } from '../../../../components/FormComponents';
import useStyles from '../../../../hooks/useStyles';
import styles from '../../../../layouts/pages/style';
import { generateMenuItems, highRiskAccountMetric, highRiskAccountOptions } from './constants';

function HighRiskAccountsConfig() {
  const dispatch = useDispatch();
  const highRiskAccountConfig = useSelector((state) => state.siteConfiguration?.data?.highRiskAccountConfig);
  const [metric, setMetric] = useState('');
  const [kpiScore, setKpiScore] = useState('');
  const [licenseUtilization, setLicenseUtilization] = useState('');
  const [enableSave, setEnableSave] = useToggle();

  useEffect(() => {
    if (highRiskAccountConfig?.metric?.value) {
      setMetric(highRiskAccountConfig.metric.value);
    }
    if (highRiskAccountConfig?.kpiScore) {
      setKpiScore(highRiskAccountConfig.kpiScore);
    }
    if (highRiskAccountConfig?.licenseUtilization) {
      setLicenseUtilization(highRiskAccountConfig.licenseUtilization);
    }
  }, [highRiskAccountConfig]);

  const generateCondition = (string, values) => {
    let condition = string;

    if (values?.kpiScore) {
      condition = condition.replace('kpiScoreValue', values.kpiScore);
    }
    if (values?.licenseUtilization) {
      condition = condition.replace('licenseUtilizationValue', values.licenseUtilization);
    }

    return condition;
  };

  const handleSubmit = () => {
    const values = { metric, kpiScore, licenseUtilization };

    const metricValue = highRiskAccountMetric.find((item) => item.value === values.metric);
    const string = highRiskAccountOptions[values.metric].value;
    const condition = generateCondition(string, values);

    if ((highRiskAccountOptions[values.metric].kpiScore && !values.kpiScore) || (highRiskAccountOptions[values.metric].licenseUtilization && !values.licenseUtilization)) {
      dispatch(openSnackbar({ open: true, message: 'Please fill all the required fields!', type: 'error' }));
      return;
    }

    const highRiskAccount = {
      condition,
      metric: metricValue,
      ...(highRiskAccountOptions[values.metric].kpiScore && { kpiScore: values.kpiScore }),
      ...(highRiskAccountOptions[values.metric].licenseUtilization && { licenseUtilization: values.licenseUtilization }),
      ...(highRiskAccountOptions[values.metric].kpiTrend && { kpiTrend: 'falling' }),
    };

    dispatch(saveSiteConfiguration({ highRiskAccountConfig: highRiskAccount }))
      .then(() => {
        dispatch(openSnackbar({ open: true, message: 'Successfully Updated!', type: 'success' }));
      });

    if (!highRiskAccountOptions[metric]?.kpiScore) {
      setKpiScore('');
    }
    if (!highRiskAccountOptions[metric]?.licenseUtilization) {
      setLicenseUtilization('');
    }
    setEnableSave(false);
  };

  const handleSaveEnable = () => {
    if (!enableSave) setEnableSave(true);
  };

  const handleChangeMetric = (e) => {
    setMetric(e.target.value);
    handleSaveEnable();
  };

  const handleChangeKpi = (e) => {
    setKpiScore(e.target.value);
    handleSaveEnable();
  };

  const handleChangeLicenseUtil = (e) => {
    setLicenseUtilization(e.target.value);
    handleSaveEnable();
  };

  const classes = useStyles(styles)();

  return (
    <Box id="high-risk-config" className={classes.cardStyles} sx={{ width: '100%' }}>
      {/* Header */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
        <Typography sx={{ fontSize: '22px', fontWeight: 500 }}>
          High-Risk Accounts
        </Typography>
        {enableSave && <Button size="small" variant="outlined" onClick={handleSubmit}>Save</Button>}
      </Stack>

      {/* Content */}
      <Grid container gap={2}>
        <InfoChip icon label={`High-risk accounts are calculate using the selected metric: ${highRiskAccountConfig?.metric?.name}`} />

        <Grid item container display="flex" justifyContent="space-between" alignItems="flex-end">
          <Grid item xs={8}>
            <Typography>Threshold Metric</Typography>
            <Typography fontSize="small">Calculations will be based on the selected metric, such as Low Score, Low Utilization, Declining Trend, or a combination of these</Typography>
          </Grid>

          <Grid item xs={3}>
            <SelectBox
              name="metric"
              title="Metric"
              data={highRiskAccountMetric}
              value={metric}
              onChange={handleChangeMetric}
            />
          </Grid>
        </Grid>

        <Grid item container display="flex" justifyContent="space-between" alignItems="flex-end">
          <Grid item xs={8}>
            <Typography>KPI Score Threshold</Typography>
            <Typography fontSize="small">This value sets the performance benchmark for the KPI score</Typography>
          </Grid>

          <Grid item xs={3}>
            <SelectBox
              disabled={!highRiskAccountOptions[metric]?.kpiScore ?? true}
              name="kpiScore"
              title="KPI Score"
              data={generateMenuItems(5, 30, 5)}
              value={kpiScore}
              onChange={handleChangeKpi}
            />
          </Grid>
        </Grid>

        <Grid item container display="flex" justifyContent="space-between" alignItems="flex-end">
          <Grid item xs={8}>
            <Typography>License Utilization Threshold</Typography>
            <Typography fontSize="small">Defines the percentage threshold to identify accounts with low license utilization</Typography>
          </Grid>

          <Grid item xs={3}>
            <SelectBox
              disabled={!highRiskAccountOptions[metric]?.licenseUtilization ?? true}
              name="licenseUtilization"
              title="License Utilization (%)"
              data={generateMenuItems(5, 90, 5)}
              value={licenseUtilization}
              onChange={handleChangeLicenseUtil}
            />
          </Grid>
        </Grid>
      </Grid>
    </Box>
  );
}

export default HighRiskAccountsConfig;
